import { Injectable, Injector, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Data, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

import { Breadcrumb, MultiBreadcrumbEntry } from './breadcrumb.model';
import { BreadcrumbsResolverService } from './breadcrumb-resolver.service';

@Injectable({
    providedIn: 'root'
})

//FROM : https://marco.dev/angular-breadcrumb

export class BreadcumbsService {

    //Emits breadcrumb hierarchy
    private readonly _breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);

    //Observable exposing it
    readonly breadcrumbs$ = this._breadcrumbs$.asObservable();

    // Injected lazily (via Injector, not constructor param) to avoid a
    // circular DI dependency risk between this root-provided service and
    // the resolver — neither currently imports the other, but this keeps
    // it that way structurally rather than relying on import order.
    private readonly injector = inject(Injector);

    constructor(private router: Router){
        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd)
        ).subscribe(event => {
            this.rebuild();
        })
    }

    private rebuild(): void {
        const root = this.router.routerState.snapshot.root;
        const breadcrumbs: Breadcrumb[] = [];
        this.addBreadcrumb(root, [], breadcrumbs);
        this._breadcrumbs$.next(breadcrumbs);
    }

    // Re-resolves and republishes the breadcrumb trail for the CURRENT route
    // without a real navigation. Needed when data a breadcrumb depends on
    // changes in place (e.g. a measurement moved to a different stage via
    // the change-stage dialog) — normal navigation-driven resolution
    // wouldn't otherwise re-run, since the URL itself doesn't change.
    refresh(): void {
        const root = this.router.routerState.snapshot.root;
        const breadcrumbs: Breadcrumb[] = [];
        this.addBreadcrumbAsync(root, [], breadcrumbs).subscribe(() => {
            this._breadcrumbs$.next(breadcrumbs);
        });
    }

    private addBreadcrumb(route: ActivatedRouteSnapshot, parentUrl: string[], breadcrumbs: Breadcrumb[]) {
        if(route) {

            //construct route URL
            const routeUrl = parentUrl.concat(route.url.map(url => url.path));
            if(route.data.breadcrumb) {
                const label = this.getLabel(route.data);
                this.pushCrumbs(label, routeUrl, breadcrumbs);
            }

            //another element for next route
            this.addBreadcrumb(route.firstChild, routeUrl, breadcrumbs);
        }
    }

    // Async counterpart of addBreadcrumb used only by refresh(): for any
    // segment configured with `resolve: { data: BreadcrumbsResolverService }`,
    // re-invokes the resolver fresh instead of reading the (stale, cached)
    // route.data.data left over from the last real navigation. Segments are
    // resolved strictly in order (switchMap, not forkJoin/merge) because
    // pushCrumbs appends to `breadcrumbs` in place and crumb order must
    // match route depth, root-first.
    private addBreadcrumbAsync(route: ActivatedRouteSnapshot, parentUrl: string[], breadcrumbs: Breadcrumb[]): Observable<void> {
        if (!route) return of(undefined);

        const routeUrl = parentUrl.concat(route.url.map(url => url.path));
        const hasResolver = !!route.routeConfig?.resolve?.['data'];

        let resolveThisSegment$: Observable<void>;
        if (!route.data.breadcrumb) {
            resolveThisSegment$ = of(undefined);
        } else if (hasResolver) {
            const resolverService = this.injector.get(BreadcrumbsResolverService);
            resolveThisSegment$ = resolverService.resolve(route).pipe(
                switchMap((resolvedData) => {
                    // Mirror what Angular's resolver would have written to
                    // route.data, so the breadcrumb function (data => data.data)
                    // reads the fresh value the same way it does after a real nav.
                    const patchedData = { ...route.data, data: resolvedData };
                    this.pushCrumbs(this.getLabel(patchedData), routeUrl, breadcrumbs);
                    return of(undefined);
                }),
            );
        } else {
            this.pushCrumbs(this.getLabel(route.data), routeUrl, breadcrumbs);
            resolveThisSegment$ = of(undefined);
        }

        return resolveThisSegment$.pipe(
            switchMap(() => this.addBreadcrumbAsync(route.firstChild, routeUrl, breadcrumbs)),
        );
    }

    private pushCrumbs(label: unknown, routeUrl: string[], breadcrumbs: Breadcrumb[]): void {
        if (Array.isArray(label)) {
            // one route segment representing a chain of entities (e.g. a
            // nested stage's ancestor path) — one crumb per entry, each
            // linking to its own URL rather than this route's URL.
            for (const entry of label as MultiBreadcrumbEntry[]) {
                breadcrumbs.push({
                    label: entry.label,
                    url: entry.url,
                    uid: entry.label + '-' + entry.url,
                });
            }
        } else {
            const breadcrumb = {
                label: label as string,
                url: '/' + routeUrl.join('/'),
                uid: label + '-' + routeUrl.join('/')
            };
            breadcrumbs.push(breadcrumb);
        }
    }

    private getLabel(data: Data) {
        return typeof data.breadcrumb === 'function' ? data.breadcrumb(data) : data.breadcrumb;
    }

}